
import { ReactNode, useEffect, useRef, useState } from 'react';

interface AnimatedCounterProps {
  endValue: number;
  duration?: number;
  precision?: number;
  suffix?: ReactNode;
  prefix?: ReactNode;
  className?: string;
}

export function AnimatedCounter({
  endValue,
  duration = 1000,
  precision = 0,
  suffix,
  prefix,
  className,
}: AnimatedCounterProps) {
  const [count, setCount] = useState(0);
  const frameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const animate = (timestamp: number) => {
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
    }

    const progress = timestamp - startTimeRef.current;
    const percentage = Math.min(progress / duration, 1);
    const currentCount = percentage * endValue;

    setCount(currentCount);

    if (progress < duration) {
      frameRef.current = requestAnimationFrame(animate);
    }
  };

  useEffect(() => {
    frameRef.current = requestAnimationFrame(animate);
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      startTimeRef.current = null;
    };
  }, [endValue, duration]);

  return (
    <span className={className}>
      {prefix}
      <span>
        {count.toFixed(precision)}
        {suffix}
      </span>
    </span>
  );
}
